/**
 * A conferência do que falta para emitir, rodando sozinha enquanto a pessoa preenche (#604).
 *
 * É ela que decide os botões de gerar: sem ela, a pessoa só descobria o que faltava depois de
 * pedir a prévia, que renderiza o documento inteiro. Por isso a conferência usa a rota que não
 * renderiza, e a prévia, quando vem, só entra como a resposta mais recente.
 *
 * @version 2026.1.0
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { PrerequisiteBlockProps } from '../../../components/common';
import documentService from '../../../services/documentService';
import { isApiError } from '../../../types/common';
import type { CheckResult, EmissionRequest } from '../../../types/document';
import { prerequisiteFromError } from '../../../utils/prerequisiteFromError';

export type EstadoDaVerificacao = 'incompleto' | 'verificando' | 'conferido' | 'recusado' | 'falhou';

export interface VerificacaoDaEmissao {
  estado: EstadoDaVerificacao;
  /** A resposta mais recente, venha da conferência ou da prévia. */
  resultado: CheckResult | null;
  /** Só no estado `recusado`: o bloqueio que o servidor explicou. */
  recusa: PrerequisiteBlockProps | null;
  /**
   * A última resposta bem-sucedida para o pedido de agora, mesmo que a conferência mais recente
   * tenha falhado. É o que mantém a versão anunciada quando a rede cai ou enquanto a volta de
   * outra aba confere de novo; `reverificar` a esquece, porque depois de emitir a versão
   * conhecida já foi gasta.
   */
  resultadoConhecido: CheckResult | null;
  /** O pedido de agora, como a prévia o guarda no clique para conferir na chegada. */
  pedidoVigente: string | null;
  /** Se o pedido guardado no clique ainda é o de agora — o que decide se a resposta vale. */
  pedidoAindaVale: (pedidoDoClique: string) => boolean;
  /**
   * Quantas respostas de conferência já chegaram. Quem mostra um aviso nascido de outra rota
   * guarda este número: resposta mais nova da conferência substitui o aviso.
   */
  respostas: number;
  /** O mesmo número, lido na hora — para quem o guarda dentro de uma chamada assíncrona. */
  respostasAgora: () => number;
  /**
   * Para a prévia publicar a lista dela, que passa a ser a mais recente. Devolve `false`, sem
   * gravar nada, quando o pedido mudou desde o clique: a prévia é de um pedido que já não vale.
   */
  registrarResultado: (pedidoDoClique: string, resultado: CheckResult) => boolean;
  /** Confere de novo agora, sem esperar o intervalo — depois de emitir, por exemplo. */
  reverificar: () => void;
  /** Quem abre um link de resolver chama isto; é o que faz a volta à aba conferir de novo. */
  marcarSaidaParaResolver: () => void;
}

/** A resposta do servidor, presa ao pedido que a pediu. */
interface Conferencia {
  chave: string;
  estado: 'conferido' | 'recusado' | 'falhou';
  resultado: CheckResult | null;
  recusa: PrerequisiteBlockProps | null;
}

/** Espera depois da última mudança: digitar uma data não pode disparar uma conferência por tecla. */
const ESPERA_MS = 500;

const TITULO_DA_RECUSA = 'Não é possível conferir a emissão com estes dados';

/** Excesso de pedidos: o limitador recusou a conferência, não o conteúdo do pedido. */
const LIMITE_DE_PEDIDOS = 429;

/**
 * Recusa de regra de negócio (4xx) trava a emissão, e a tela mostra o motivo. Falha de rede,
 * erro do servidor (5xx) e o limite de pedidos (429) não: o servidor recompõe tudo na geração
 * e recusa o que impede, e travar por um aviso perdido deixaria o sistema parado.
 */
function recusaDoServidor(err: unknown): PrerequisiteBlockProps | null {
  if (
    !isApiError(err) ||
    err.status === undefined ||
    err.status < 400 ||
    err.status >= 500 ||
    err.status === LIMITE_DE_PEDIDOS
  ) {
    return null;
  }

  return prerequisiteFromError(err) ?? { title: TITULO_DA_RECUSA, reason: err.message };
}

/**
 * @param pedido o pedido da prévia, ou `null` enquanto falta campo obrigatório. O conteúdo é
 *   comparado, não a identidade: o mesmo pedido montado de novo não refaz a conferência.
 * @param aoVoltarDeResolver roda quando a pessoa volta à aba depois de ter aberto um link de
 *   resolver — é onde a tela recarrega as opções que o cadastro pode ter mudado.
 */
export function useVerificacaoDaEmissao(
  pedido: EmissionRequest | null,
  aoVoltarDeResolver: () => void
): VerificacaoDaEmissao {
  const [rodada, setRodada] = useState(0);
  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const [respostas, setRespostas] = useState(0);
  const respostasAtuais = useRef(0);
  const [conhecido, setConhecido] = useState<{ pedido: string; resultado: CheckResult } | null>(
    null
  );

  const pedidoVigente = pedido === null ? null : JSON.stringify(pedido);
  const chave = pedidoVigente === null ? null : `${rodada}|${pedidoVigente}`;
  const chaveAtual = useRef(chave);
  const pedidoVigenteAtual = useRef(pedidoVigente);
  const pedidoAtual = useRef(pedido);
  const aoVoltar = useRef(aoVoltarDeResolver);
  const saiuParaResolver = useRef(false);
  // A volta à aba e o pedido de reverificar não esperam: a pessoa já parou de digitar.
  const semEspera = useRef(false);

  useEffect(() => {
    chaveAtual.current = chave;
    pedidoVigenteAtual.current = pedidoVigente;
    pedidoAtual.current = pedido;
    aoVoltar.current = aoVoltarDeResolver;
  });

  /** Toda resposta passa por aqui, para a contagem andar junto com ela. */
  const publicar = useCallback((nova: Conferencia): void => {
    respostasAtuais.current += 1;
    setRespostas(respostasAtuais.current);
    setConferencia(nova);
  }, []);

  useEffect(() => {
    const imediata = semEspera.current;
    semEspera.current = false;

    const pedidoDaRodada = pedidoAtual.current;
    if (chave === null || pedidoDaRodada === null) return;
    const pedidoSerializado = JSON.stringify(pedidoDaRodada);

    const controle = new AbortController();
    const temporizador = setTimeout(
      () => {
        documentService
          .check(pedidoDaRodada, controle.signal)
          .then((resposta) => {
            if (controle.signal.aborted) return;
            publicar({ chave, estado: 'conferido', resultado: resposta, recusa: null });
            setConhecido({ pedido: pedidoSerializado, resultado: resposta });
          })
          .catch((err: unknown) => {
            if (controle.signal.aborted) return;
            const bloqueio = recusaDoServidor(err);
            publicar({
              chave,
              estado: bloqueio ? 'recusado' : 'falhou',
              resultado: null,
              recusa: bloqueio,
            });
          });
      },
      imediata ? 0 : ESPERA_MS
    );

    return () => {
      clearTimeout(temporizador);
      controle.abort();
    };
  }, [chave, publicar]);

  /** Confere de novo sem esquecer a versão conhecida: o pedido é o mesmo, e nada foi emitido. */
  const conferirDeNovo = useCallback((): void => {
    semEspera.current = true;
    setRodada((atual) => atual + 1);
  }, []);

  const reverificar = useCallback((): void => {
    setConhecido(null);
    conferirDeNovo();
  }, [conferirDeNovo]);

  useEffect(() => {
    const aoMudarDeAba = (): void => {
      if (document.visibilityState !== 'visible' || !saiuParaResolver.current) return;
      saiuParaResolver.current = false;
      conferirDeNovo();
      aoVoltar.current();
    };

    document.addEventListener('visibilitychange', aoMudarDeAba);
    return () => document.removeEventListener('visibilitychange', aoMudarDeAba);
  }, [conferirDeNovo]);

  const registrarResultado = useCallback((pedidoDoClique: string, novo: CheckResult): boolean => {
    const vigente = chaveAtual.current;
    if (vigente === null || pedidoVigenteAtual.current !== pedidoDoClique) return false;
    publicar({ chave: vigente, estado: 'conferido', resultado: novo, recusa: null });
    setConhecido({ pedido: pedidoDoClique, resultado: novo });
    return true;
  }, [publicar]);

  const pedidoAindaVale = useCallback(
    (pedidoDoClique: string): boolean => pedidoVigenteAtual.current === pedidoDoClique,
    []
  );

  const respostasAgora = useCallback((): number => respostasAtuais.current, []);

  const marcarSaidaParaResolver = useCallback((): void => {
    saiuParaResolver.current = true;
  }, []);

  // Derivado, e não guardado: a conferência só vale para o pedido que a pediu. Pedido novo é
  // "verificando" desde o primeiro render, sem esperar efeito nenhum zerar o estado.
  const vale = chave !== null && conferencia?.chave === chave;
  const estado: EstadoDaVerificacao =
    chave === null ? 'incompleto' : vale && conferencia ? conferencia.estado : 'verificando';
  const resultado = vale && conferencia ? conferencia.resultado : null;
  const recusa = vale && conferencia ? conferencia.recusa : null;
  const resultadoConhecido =
    pedidoVigente !== null && conhecido?.pedido === pedidoVigente ? conhecido.resultado : null;

  return {
    estado,
    resultado,
    recusa,
    resultadoConhecido,
    pedidoVigente,
    pedidoAindaVale,
    respostas,
    respostasAgora,
    registrarResultado,
    reverificar,
    marcarSaidaParaResolver,
  };
}
